"use client";

import { useCallback } from "react";

export type DatafastParams = Record<string, string | number | boolean | null | undefined>;

const sanitize = (goal: string, params?: DatafastParams) => {
  const cleanGoal = goal
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .slice(0, 32);

  if (!params) {
    return { goal: cleanGoal, params: undefined } as const;
  }

  const out: Record<string, string> = {};
  const keys = Object.keys(params).slice(0, 10);

  for (const key of keys) {
    const cleanKey = key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "_")
      .slice(0, 32);

    const value = params[key];
    if (value === undefined) continue;

    const asString = String(value).slice(0, 255);
    if (cleanKey.length > 0 && asString.length > 0) {
      out[cleanKey] = asString;
    }
  }

  return {
    goal: cleanGoal,
    params: Object.keys(out).length ? out : undefined,
  } as const;
};

export function useDatafast() {
  const track = useCallback(async (goal: string, params?: DatafastParams) => {
    if (typeof window === "undefined") return;

    const { goal: sanitizedGoal, params: sanitizedParams } = sanitize(goal, params);
    const metadata: Record<string, string> = {
      ...(sanitizedParams ?? {}),
      path: window.location.pathname.slice(0, 120),
    };

    try {
      await fetch("/api/datafast/goal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: sanitizedGoal, metadata }),
        cache: "no-store",
        keepalive: true,
      });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Failed to send Datafast event", error);
      }
    }
  }, []);

  return { track } as const;
}

export const __private__ = { sanitize };
